import { Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AddressCaptureSessionService } from '../services/address-capture-session.service';
import { RestaurantService } from '../services/restaurant.service';
import {
    GEO_CALLER_CONFIG,
    GEO_CALLER_HEADER,
    GEO_CORRELATION_HEADER,
    inRollout,
    isUuid,
    matchGeoCallerRule,
    readCallerSwitch,
} from './geo-caller';

/**
 * Carimba `X-MP-Caller` (e `X-Correlation-Id`, nas chamadas de endereço) nas chamadas de mapa/frete à
 * API própria, para o mp-geospatial atribuir o custo ao pedir.delivery. TODA a regra (rotas, hosts,
 * dial, desligador, o custo de preflight e por que o default é 10%) está em `geo-caller.ts`.
 *
 * Um interceptor, e não edição de cada chamada, porque o `ApiService.parseHeaders` DESCARTA qualquer
 * header que o chamador passe — o interceptor é o único ponto por onde cabeçalho entra, e uma rota
 * nova nasce sem ele até alguém a listar em `GEO_CALLER_RULES`.
 *
 * ⚠️ **Nunca lança e nunca altera o fluxo.** Este interceptor está na frente do frete e do
 * autocomplete do checkout; um erro aqui derrubaria TODA requisição do HttpClient (o cardápio
 * inclusive). Por isso: o construtor só guarda o `Injector` (não resolve nada, não tem como falhar);
 * toda dependência é resolvida na hora da requisição, dentro do `try`; e qualquer falha devolve a
 * requisição ORIGINAL, sem o cabeçalho. `next.handle` fica FORA do `try` de propósito — o erro de uma
 * requisição é do chamador, não nosso para engolir.
 *
 * Só no browser: no SSR/prerender (Node, `window` inexistente) é inerte. Quem renderiza no servidor é
 * o servidor, não um cliente — carimbar ali contaminaria o `geo_cost` com chamadas que não são de
 * ninguém, e o dial por loja nem faria sentido.
 */
@Injectable()
export class GeoCallerInterceptor implements HttpInterceptor {
    constructor(private injector: Injector) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(this.comCabecalhos(request));
    }

    private comCabecalhos(request: HttpRequest<any>): HttpRequest<any> {
        try {
            // Browser primeiro, e as duas guardas de propósito: `window` sozinho não basta num render de
            // servidor que o polyfille, e `PLATFORM_ID` sozinho exigiria o `Injector` até no Node.
            if (typeof window === 'undefined') return request;
            if (!isPlatformBrowser(this.injector.get(PLATFORM_ID))) return request;
            // Barato antes do caro: este método roda em TODA requisição (cardápio, cupom, pedido...).
            if (!(Number(GEO_CALLER_CONFIG.rolloutPercent) > 0)) return request;

            const rule = matchGeoCallerRule(request.method, request.url);
            if (!rule) return request;

            const chave = readCallerSwitch();
            if (chave === 'off') return request;
            if (chave !== 'on' && !inRollout(this.restaurantId(), GEO_CALLER_CONFIG.rolloutPercent)) return request;

            const setHeaders: { [nome: string]: string } = {};
            // Quem já escolheu o rótulo (futuro) manda mais que a tabela.
            if (!request.headers.has(GEO_CALLER_HEADER)) setHeaders[GEO_CALLER_HEADER] = rule.caller;
            if (rule.correlation && !request.headers.has(GEO_CORRELATION_HEADER)) {
                // Lido AGORA, na hora da requisição: é o id que a telemetria vai carimbar nos eventos
                // desta mesma sessão. Vazio (sem sessão) ou fora do formato = só o rótulo.
                const id = this.injector.get(AddressCaptureSessionService).id;
                if (isUuid(id)) setHeaders[GEO_CORRELATION_HEADER] = id;
            }
            if (Object.keys(setHeaders).length === 0) return request;

            return request.clone({ setHeaders });
        } catch (e) {
            // Atribuir custo nunca pode quebrar a requisição que a pediu.
            return request;
        }
    }

    private restaurantId(): number {
        const restaurante: any = this.injector.get(RestaurantService).restaurant;
        return Number(restaurante && restaurante.info && restaurante.info.id) || 0;
    }
}
